import {
  LayoutDashboard, Inbox, CalendarDays, UserPlus, Users, BookOpen, FileText,
  CheckCircle, ClipboardList, Mic, Award, DollarSign, Package, BarChart,
  Bell, Folder, Sparkles, BookOpen as BookIcon, Send, Activity, Briefcase,
  Brain, Workflow, TrendingUp, Target, Lightbulb, Cpu, Shield, Layers,
  LineChart, Radio, Settings as SettingsIcon, Search, ChevronRight, Monitor, PenTool,
} from 'lucide-react'

// Source of truth: matches routes in Dashboard.jsx <Routes> block.
// Normal academy operations, content, and management pages are fully visible
// and searchable. Only internal AI engineering, diagnostics, and platform
// governance machinery remain hidden from primary navigation.

export const NAVIGATION = [
  // ─── ACADEMY ──────────────────────────────────────────────────────────────
  {
    id: 'academy',
    label: 'Academy',
    children: [
      { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/admin/admissions', icon: Inbox, label: 'Admissions' },
      { to: '/admin/intakes', icon: CalendarDays, label: 'Intakes' },
      { to: '/admin/students', icon: Users, label: 'Students' },
      { to: '/admin/courses', icon: BookOpen, label: 'Courses' },
      { to: '/admin/assignments', icon: FileText, label: 'Assignments' },
      { to: '/admin/certificates', icon: Award, label: 'Certificates' },
      { to: '/admin/attendance', icon: CheckCircle, label: 'Attendance' },
      { to: '/admin/trainers', icon: Briefcase, label: 'Trainers' },
      { to: '/admin/academic-monitor', icon: Radio, label: 'Academic Monitor' },
      { to: '/admin/masterclass', icon: Award, label: 'Masterclass' },
      { to: '/admin/project-lab', icon: Layers, label: 'Project Lab' },
      { to: '/admin/creative-tools', icon: PenTool, label: 'Creative Tools' },
    ],
  },

  // ─── CONTENT & ENGAGEMENT ──────────────────────────────────────────────────
  {
    id: 'content',
    label: 'Content & Engagement',
    children: [
      { to: '/admin/content', icon: Folder, label: 'Content' },
      { to: '/admin/resources', icon: BookIcon, label: 'Resources' },
      { to: '/admin/communications', icon: Send, label: 'Communications' },
      { to: '/admin/social-studio', icon: Sparkles, label: 'Social Studio' },
      { to: '/admin/calendar', icon: CalendarDays, label: 'Calendar' },
    ],
  },

  // ─── FINANCE & REPORTS ─────────────────────────────────────────────────────
  {
    id: 'finance-reports',
    label: 'Finance & Reports',
    children: [
      { to: '/admin/finance', icon: DollarSign, label: 'Finance' },
      { to: '/admin/reports', icon: BarChart, label: 'Reports' },
      { to: '/admin/ai-reports', icon: Sparkles, label: 'AI Reports' },
      { to: '/admin/ai-tutor', icon: Sparkles, label: 'AI Usage & Cost' },
    ],
  },

  // ─── OPERATIONS ───────────────────────────────────────────────────────────
  {
    id: 'operations',
    label: 'Operations',
    children: [
      { to: '/admin/ai-actions', icon: Activity, label: 'Action Center' },
      { to: '/admin/equipment', icon: Package, label: 'Equipment' },
      { to: '/admin/digital-lab', icon: Monitor, label: 'Digital Lab' },
      { to: '/admin/users', icon: Shield, label: 'User Management' },
      { to: '/admin/ai-admin', icon: Sparkles, label: 'Academy Copilot' },
      { to: '/admin/settings', icon: SettingsIcon, label: 'Settings' },
    ],
  },
]

// Specialist and internal AI engineering routes — hidden from primary sidebar
// and search, but remain functional via direct URL for authorized system engineers.
export const SPECIALIST_ITEMS = [
  { to: '/admin/ai-os', icon: Layers, label: 'Academy OS Cockpit', parents: [{ label: 'Diagnostics' }] },
  { to: '/admin/ai-governance', icon: Shield, label: 'AI Governance', parents: [{ label: 'Governance' }] },
  { to: '/admin/ai-autonomy', icon: Cpu, label: 'AI Autonomy Control', parents: [{ label: 'Governance' }] },
  { to: '/admin/ai-assurance', icon: Shield, label: 'AI Quality Assurance', parents: [{ label: 'Governance' }] },
  { to: '/admin/ai-cases', icon: Briefcase, label: 'AI Cases', parents: [{ label: 'Operations' }] },
  { to: '/admin/ai-communications', icon: Send, label: 'AI Communications Engine', parents: [{ label: 'Operations' }] },
  { to: '/admin/ai-workflows', icon: Workflow, label: 'AI Workflows', parents: [{ label: 'Operations' }] },
  { to: '/admin/ai-engagement', icon: Target, label: 'AI Engagement', parents: [{ label: 'Operations' }] },
  { to: '/admin/ai-outcomes', icon: TrendingUp, label: 'AI Outcomes', parents: [{ label: 'Outcomes' }] },
  { to: '/admin/ai-intelligence', icon: Brain, label: 'AI Intelligence', parents: [{ label: 'Intelligence' }] },
  { to: '/admin/ai-knowledge', icon: BookOpen, label: 'AI Knowledge', parents: [{ label: 'Intelligence' }] },
  { to: '/admin/ai-forecasting', icon: TrendingUp, label: 'AI Forecasting', parents: [{ label: 'Intelligence' }] },
  { to: '/admin/ai-personalization', icon: Lightbulb, label: 'AI Personalization', parents: [{ label: 'Intelligence' }] },
  { to: '/admin/logs', icon: Activity, label: 'System Logs', parents: [{ label: 'System' }] },
  { to: '/admin/pending-registrations', icon: UserPlus, label: 'Pending Registrations', parents: [{ label: 'Legacy' }] },
]

export const DASHBOARD_ITEM = { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' }

export const NAVIGATION_WITH_DASHBOARD = NAVIGATION

// Default pinned items for administrators (max 7).
export const DEFAULT_PINNED = [
  '/admin/dashboard',
  '/admin/admissions',
  '/admin/intakes',
  '/admin/students',
  '/admin/courses',
  '/admin/attendance',
  '/admin/ai-actions',
]

export const MAX_PINNED = 7
export const PINNED_STORAGE_KEY = 'sma.admin.pinned'
export const EXPANDED_STORAGE_KEY = 'sma.admin.expanded'

// Flatten navigation into a list of leaf items (for search & pin lookups).
// By default, specialist/diagnostic pages are EXCLUDED so normal administrator
// navigation and search only exposes genuine operational destinations.
export function flattenNav(items = NAVIGATION, includeSpecialist = false) {
  const result = []
  const walk = (nodes, parents = []) => {
    for (const node of nodes) {
      if (node.to) {
        result.push({ ...node, parents })
      } else if (node.children) {
        walk(node.children, [...parents, node])
      }
    }
  }
  walk(items)
  if (includeSpecialist && Array.isArray(SPECIALIST_ITEMS)) {
    for (const spec of SPECIALIST_ITEMS) {
      if (!result.some((r) => r.to === spec.to)) {
        result.push(spec)
      }
    }
  }
  return result
}

// Find the category (and sub-group) path for a given route.
export function findCategoryPath(route, items = NAVIGATION) {
  const path = []
  const walk = (nodes, trail) => {
    for (const node of nodes) {
      const next = [...trail, node]
      if (node.to === route) {
        path.push(...next)
        return true
      }
      if (node.children && walk(node.children, next)) {
        return true
      }
    }
    return false
  }
  walk(items, [])
  return path
}

// Check if a route matches the current path (exact or nested, with the
// /admin/dashboard exception so /admin doesn't highlight Dashboard).
export function isRouteActive(currentPath, route) {
  if (route === '/admin/dashboard') return currentPath === '/admin/dashboard' || currentPath === '/admin'
  return currentPath === route || currentPath.startsWith(`${route}/`)
}

export { Search, ChevronRight }
